import { z } from 'zod';

/**
 * Customer team member — one effective period of one user's membership on
 * one customer's team. Billing rate is not a membership attribute; an entry's
 * rate resolves from the customer's SOW T&M line (else the global rate card).
 */
export const CustomerTeamMemberSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  userId: z.string().uuid(),
  serviceRoleId: z.string().uuid(),
  teamLead: z.boolean(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
/** Base membership row — the shape of write responses (create/patch/end/
 *  restore). Read responses use {@link CustomerTeamMemberWithLabelsSchema}. */
export type CustomerTeamMember = z.infer<typeof CustomerTeamMemberSchema>;

/**
 * A membership enriched with the display labels the team tab + user-detail
 * customer section render, resolved server-side. Returned by the read
 * endpoints (in-effect / all / by-id, and `listForUserWithLabels`). The
 * labels are always present on these responses; `null` means the referenced
 * user/role couldn't be resolved (e.g. deleted) — render as "Unknown", not a
 * loading placeholder. Keeping this distinct from the base type means a
 * consumer can't mistake an un-enriched write response for "labels missing".
 */
export const CustomerTeamMemberWithLabelsSchema = CustomerTeamMemberSchema.extend({
  userName: z.string().nullable(),
  serviceRoleName: z.string().nullable(),
});
export type CustomerTeamMemberWithLabels = z.infer<typeof CustomerTeamMemberWithLabelsSchema>;

export const CreateCustomerTeamMemberInput = z.object({
  userId: z.string().uuid(),
  serviceRoleId: z.string().uuid(),
  teamLead: z.boolean().optional(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable().optional(),
});
export type CreateCustomerTeamMemberInput = z.infer<typeof CreateCustomerTeamMemberInput>;

// `userId` is NOT patchable — it's identity for a membership row (move to a
// different user by ending the existing one and creating a new one).
// `effectiveFrom` IS patchable: the start date can be corrected in place. The
// server runs a post-merge `effectiveTo > effectiveFrom` check and the GIST
// no-overlap constraint still applies, so a shift that collides 409s.
export const PatchCustomerTeamMemberInput = z
  .object({
    serviceRoleId: z.string().uuid().optional(),
    teamLead: z.boolean().optional(),
    effectiveFrom: z.string().optional(),
    effectiveTo: z.string().nullable().optional(),
  })
  .strict();
export type PatchCustomerTeamMemberInput = z.infer<typeof PatchCustomerTeamMemberInput>;

// `effectiveTo` is required — the server runs UTC and can't reliably
// default to "today" in the caller's local timezone.
export interface EndCustomerTeamMemberInput {
  effectiveTo: string;
}

/** One row of the team import report, in upload order. */
export type ImportRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportTeamRow {
  rowNumber: number;
  qbId: string | null;
  email: string | null;
  name: string | null;
  outcome: ImportRowOutcome;
  errors?: string[];
}

export interface ImportTeamResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportTeamRow[];
}
